// Encrypted local storage (spec §114, §55): wraps an object store (IndexedDB in the web/PWA build,
// memory in tests) so every row, snapshot, file and private metadata value is stored as AES-GCM
// ciphertext.
//
// Keys: a random 256-bit data key (DEK) encrypts the data. It is stored only WRAPPED — once by a key
// derived from the advisor's passphrase (PBKDF2-SHA-256, 600 000 iterations) and once by a random
// recovery key shown to the advisor at setup. Neither the passphrase, the recovery key nor the DEK
// is ever written in plaintext. After unlock the DEK is held as a non-extractable CryptoKey in memory
// only; locking (or auto-lock) drops it.
//
// Honest limits: encryption at rest does not replace device security (screen lock, OS updates) or
// server-side authorisation, and a forgotten passphrase + lost recovery key means the local data can
// only be restored from a backup. The native desktop build uses its own SQLite file (SQLCipher is on
// the roadmap); this wrapper protects the browser/tablet build.
import { TABLE_NAMES, type Changeset, type Dataset, type TableName } from '@vero/domain';
import { StorageError, fromBase64, toBase64, type FileMeta, type SnapshotMeta, type StorageAdapter } from './adapter.ts';

const subtle = () => globalThis.crypto.subtle;
const enc = new TextEncoder(), dec = new TextDecoder();
export const CRYPTO_META = 'crypto';
const PLAIN_META = new Set(['schemaVersion', CRYPTO_META]);
export const DEFAULT_KDF_ITERATIONS = 600_000;

interface Wrapped { iv: string; ct: string }
export interface CryptoMeta { v: 1; state: 'migrating' | 'active'; kdf: { name: 'PBKDF2'; hash: 'SHA-256'; iterations: number; salt: string }; wrapped: Wrapped; recovery: { salt: string; wrapped: Wrapped }; check: Wrapped; createdAt: string; autoLockMinutes?: number }
interface EncRow { id: string; __enc: 1; iv: string; ct: string }
const isEnc = (x: unknown): x is EncRow => !!x && typeof x === 'object' && (x as EncRow).__enc === 1;

async function kek(secret: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const material = await subtle().importKey('raw', enc.encode(secret.normalize('NFKC')), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey({ name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' }, material, { name: 'AES-GCM', length: 256 }, false, ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']);
}
async function wrap(dek: CryptoKey, by: CryptoKey): Promise<Wrapped> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(await subtle().wrapKey('raw', dek, by, { name: 'AES-GCM', iv }))) };
}
async function unwrap(w: Wrapped, by: CryptoKey, extractable = false): Promise<CryptoKey> {
  return subtle().unwrapKey('raw', fromBase64(w.ct) as BufferSource, by, { name: 'AES-GCM', iv: fromBase64(w.iv) as BufferSource }, { name: 'AES-GCM', length: 256 }, extractable, ['encrypt', 'decrypt']);
}
async function seal(key: CryptoKey, bytes: Uint8Array): Promise<Wrapped> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv }, key, bytes as BufferSource))) };
}
async function open(key: CryptoKey, w: Wrapped): Promise<Uint8Array> {
  return new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(w.iv) as BufferSource }, key, fromBase64(w.ct) as BufferSource));
}
/** 24 characters from an unambiguous alphabet, grouped for writing down */
export function newRecoveryKey(): string {
  const A = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const r = crypto.getRandomValues(new Uint8Array(24));
  return [...r].map((b) => A[b % A.length]).join('').replace(/(.{4})(?=.)/g, '$1-');
}
const normRecovery = (k: string) => k.toUpperCase().replace(/[^A-Z0-9]/g, '');

export class CryptoAdapter implements StorageAdapter {
  readonly inner: StorageAdapter;
  readonly engine: string;
  private key: CryptoKey;
  private innerOpen: boolean;
  private constructor(inner: StorageAdapter, key: CryptoKey, innerOpen: boolean) { this.inner = inner; this.key = key; this.innerOpen = innerOpen; this.engine = `${inner.engine}+aes-gcm`; }

  /** Is this store encrypted? (opens the inner store if needed) */
  static async status(inner: StorageAdapter): Promise<CryptoMeta | null> { return (await inner.getMeta<CryptoMeta>(CRYPTO_META)) ?? null; }

  /**
   * Turn encryption on for an existing (plaintext) store. Everything is re-written as ciphertext in
   * one transaction; an interrupted setup resumes at the next unlock. Returns the recovery key —
   * show it once and never store it.
   */
  static async setup(inner: StorageAdapter, passphrase: string, opts: { iterations?: number; now?: string } = {}): Promise<{ adapter: CryptoAdapter; recoveryKey: string }> {
    if (passphrase.length < 10) throw new StorageError('crypto.passphrase_too_short');
    if (await CryptoAdapter.status(inner)) throw new StorageError('crypto.already_enabled');
    const iterations = opts.iterations ?? DEFAULT_KDF_ITERATIONS;
    const dek = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const salt = crypto.getRandomValues(new Uint8Array(16)), rsalt = crypto.getRandomValues(new Uint8Array(16));
    const recoveryKey = newRecoveryKey();
    const meta: CryptoMeta = {
      v: 1, state: 'migrating', kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations, salt: toBase64(salt) },
      wrapped: await wrap(dek, await kek(passphrase, salt, iterations)),
      recovery: { salt: toBase64(rsalt), wrapped: await wrap(dek, await kek(normRecovery(recoveryKey), rsalt, iterations)) },
      check: await seal(dek, enc.encode('vero-ok')), createdAt: opts.now ?? new Date().toISOString(),
    };
    const usable = await subtle().importKey('raw', await subtle().exportKey('raw', dek), 'AES-GCM', false, ['encrypt', 'decrypt']);
    await inner.setMeta(CRYPTO_META, meta);
    const adapter = new CryptoAdapter(inner, usable, true);
    await adapter.finishMigration();
    return { adapter, recoveryKey };
  }

  /** Unlock with the passphrase or the recovery key. Wrong secret → 'crypto.wrong_passphrase'. */
  static async unlock(inner: StorageAdapter, secret: { passphrase: string } | { recoveryKey: string }): Promise<CryptoAdapter> {
    const meta = await CryptoAdapter.status(inner);
    if (!meta) throw new StorageError('crypto.not_enabled');
    let dek: CryptoKey;
    try {
      dek = 'passphrase' in secret
        ? await unwrap(meta.wrapped, await kek(secret.passphrase, fromBase64(meta.kdf.salt), meta.kdf.iterations))
        : await unwrap(meta.recovery.wrapped, await kek(normRecovery(secret.recoveryKey), fromBase64(meta.recovery.salt), meta.kdf.iterations));
      if (dec.decode(await open(dek, meta.check)) !== 'vero-ok') throw new Error('check');
    } catch { throw new StorageError('crypto.wrong_passphrase'); }
    const a = new CryptoAdapter(inner, dek, true);
    if (meta.state === 'migrating') await a.finishMigration();
    return a;
  }

  /** Change the passphrase (the data is not re-encrypted; only the wrapped key changes). */
  static async changePassphrase(inner: StorageAdapter, current: { passphrase: string } | { recoveryKey: string }, next: string): Promise<void> {
    if (next.length < 10) throw new StorageError('crypto.passphrase_too_short');
    const meta = (await CryptoAdapter.status(inner))!;
    let dek: CryptoKey;
    try {
      dek = 'passphrase' in current
        ? await unwrap(meta.wrapped, await kek(current.passphrase, fromBase64(meta.kdf.salt), meta.kdf.iterations), true)
        : await unwrap(meta.recovery.wrapped, await kek(normRecovery(current.recoveryKey), fromBase64(meta.recovery.salt), meta.kdf.iterations), true);
    } catch { throw new StorageError('crypto.wrong_passphrase'); }
    const salt = crypto.getRandomValues(new Uint8Array(16));
    await inner.setMeta(CRYPTO_META, { ...meta, kdf: { ...meta.kdf, salt: toBase64(salt) }, wrapped: await wrap(dek, await kek(next, salt, meta.kdf.iterations)) });
  }

  private async finishMigration() {
    const ds = await this.loadAll(); // tolerates mixed plaintext/ciphertext rows
    await this.replaceAll(ds);
    for (const s of await this.inner.listSnapshots()) {
      const content = await this.inner.readSnapshot(s.id);
      if (content !== undefined && !content.startsWith('enc:')) await this.saveSnapshot(s, content);
    }
    for (const f of await this.inner.listFiles()) {
      const raw = await this.inner.getFile(f.id);
      if (raw && !(raw.meta as FileMeta & { __enc?: 1 }).__enc) await this.putFile(raw.meta, raw.content);
    }
    for (const k of ['sync.state']) { const val = await this.inner.getMeta(k); if (val !== undefined && !isEnc(val)) await this.setMeta(k, val); }
    const meta = (await this.inner.getMeta<CryptoMeta>(CRYPTO_META))!;
    await this.inner.setMeta(CRYPTO_META, { ...meta, state: 'active' });
  }

  private async encRow(row: { id: string }): Promise<EncRow> { const w = await seal(this.key, enc.encode(JSON.stringify(row))); return { id: row.id, __enc: 1, ...w }; }
  private async decRow(x: unknown): Promise<unknown> { return isEnc(x) ? JSON.parse(dec.decode(await open(this.key, x))) : x; }

  async open() { if (!this.innerOpen) { this.innerOpen = true; return this.inner.open(); } return { schemaVersion: Number(await this.inner.getMeta('schemaVersion') ?? 0), created: false }; }
  async loadAll(): Promise<Dataset> {
    const raw = await this.inner.loadAll();
    const out = {} as Record<TableName, unknown[]>;
    for (const t of TABLE_NAMES) out[t] = await Promise.all(((raw[t] ?? []) as unknown[]).map((r) => this.decRow(r)));
    return out as unknown as Dataset;
  }
  async commit(cs: Changeset) {
    const puts = await Promise.all(cs.puts.map(async (p) => ({ table: p.table, row: await this.encRow(p.row as { id: string }) })));
    return this.inner.commit({ ...cs, puts: puts as never });
  }
  async replaceAll(ds: Dataset) {
    const out = {} as Record<TableName, unknown[]>;
    for (const t of TABLE_NAMES) out[t] = await Promise.all(((ds[t] ?? []) as { id: string }[]).map((r) => this.encRow(r)));
    return this.inner.replaceAll(out as unknown as Dataset);
  }
  async getMeta<T>(key: string) { const v = await this.inner.getMeta<unknown>(key); return (PLAIN_META.has(key) || !isEnc(v) ? v : JSON.parse(dec.decode(await open(this.key, v)))) as T | undefined; }
  async setMeta(key: string, value: unknown) {
    if (PLAIN_META.has(key)) return this.inner.setMeta(key, value);
    return this.inner.setMeta(key, { id: key, __enc: 1, ...(await seal(this.key, enc.encode(JSON.stringify(value ?? null)))) });
  }
  async saveSnapshot(meta: SnapshotMeta, content: string) {
    const w = await seal(this.key, enc.encode(content));
    const label = await seal(this.key, enc.encode(meta.label));
    return this.inner.saveSnapshot({ ...meta, label: `enc:${label.iv}:${label.ct}` }, `enc:${w.iv}:${w.ct}`);
  }
  private async decLabel(s: SnapshotMeta): Promise<SnapshotMeta> {
    if (!s.label.startsWith('enc:')) return s;
    const [, iv, ct] = s.label.split(':');
    return { ...s, label: dec.decode(await open(this.key, { iv: iv!, ct: ct! })) };
  }
  async listSnapshots() { return Promise.all((await this.inner.listSnapshots()).map((s) => this.decLabel(s))); }
  async readSnapshot(id: string) {
    const c = await this.inner.readSnapshot(id);
    if (c === undefined || !c.startsWith('enc:')) return c;
    const [, iv, ct] = c.split(':');
    return dec.decode(await open(this.key, { iv: iv!, ct: ct! }));
  }
  deleteSnapshot(id: string) { return this.inner.deleteSnapshot(id); }
  async putFile(meta: FileMeta, content: Uint8Array) {
    const w = await seal(this.key, content);
    const m = await seal(this.key, enc.encode(JSON.stringify(meta)));
    return this.inner.putFile({ id: meta.id, name: '', mime: 'application/octet-stream', size: 0, sha256: '', createdAt: meta.createdAt, __enc: 1, m } as FileMeta, enc.encode(JSON.stringify(w)));
  }
  async getFile(id: string) {
    const f = await this.inner.getFile(id);
    if (!f) return undefined;
    const em = f.meta as FileMeta & { __enc?: 1; m?: Wrapped };
    if (!em.__enc) return f;
    const meta = JSON.parse(dec.decode(await open(this.key, em.m!))) as FileMeta;
    return { meta, content: await open(this.key, JSON.parse(dec.decode(f.content)) as Wrapped) };
  }
  deleteFile(id: string) { return this.inner.deleteFile(id); }
  async listFiles() {
    return Promise.all((await this.inner.listFiles()).map(async (f) => { const em = f as FileMeta & { __enc?: 1; m?: Wrapped }; return em.__enc ? JSON.parse(dec.decode(await open(this.key, em.m!))) as FileMeta : f; }));
  }
  close() { return this.inner.close(); }
}
